import React, { useCallback, useMemo, useState, createElement } from 'react';
import { getTenant, formatCurrencyAmount } from '../../../theme/tokens';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, UploadCloudIcon, DownloadIcon, TrashIcon, AlertCircleIcon, CheckCircleIcon, FileSpreadsheetIcon } from 'lucide-react';
const TEMPLATE_COLUMNS = [{
  key: 'supplierName',
  label: 'Supplier Name',
  hint: 'Company or individual name',
  required: true
}, {
  key: 'supplierEmail',
  label: 'Supplier Email',
  hint: 'Contact email',
  required: true
}, {
  key: 'billNumber',
  label: 'Bill Number',
  hint: 'e.g. INV-2026-001',
  required: true
}, {
  key: 'amount',
  label: 'Amount',
  hint: 'Numeric value',
  required: true
}, {
  key: 'currency',
  label: 'Currency',
  hint: `e.g. ${getTenant().currency.code}`,
  required: false
}, {
  key: 'dueDate',
  label: 'Due Date',
  hint: 'YYYY-MM-DD',
  required: true
}, {
  key: 'issueDate',
  label: 'Issue Date',
  hint: 'YYYY-MM-DD',
  required: false
}, {
  key: 'description',
  label: 'Description',
  hint: 'Bill description',
  required: false
}, {
  key: 'itemDescription',
  label: 'Item Description',
  hint: 'Line item name',
  required: false
}, {
  key: 'itemQuantity',
  label: 'Item Quantity',
  hint: 'Number',
  required: false
}, {
  key: 'itemUnitPrice',
  label: 'Item Unit Price',
  hint: 'Numeric value',
  required: false
}, {
  key: 'notes',
  label: 'Notes',
  hint: 'Additional notes',
  required: false
}];
interface ParsedRow {
  id: string;
  data: Record<string, string>;
  errors: string[];
}
function generateCSVTemplate(): string {
  const header = TEMPLATE_COLUMNS.map(c => c.label).join(',');
  const sampleRow = ['Acme Corp Ltd', 'billing@acmecorp.com', 'INV-2026-020', '45000', getTenant().currency.code, '2026-04-15', '2026-03-15', 'Monthly service fee', 'Consulting services', '1', '45000', 'Net 30 payment terms'].join(',');
  return `${header}\n${sampleRow}`;
}
function parseCSV(text: string): {
  headers: string[];
  rows: string[][];
} {
  const lines = text.split('\n').filter(l => l.trim() !== '');
  if (lines.length === 0) return {
    headers: [],
    rows: []
  };
  const headers = lines[0].split(',').map(h => h.trim());
  const rows = lines.slice(1).map(line => {
    const values: string[] = [];
    let current = '';
    let inQuotes = false;
    for (const char of line) {
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        values.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    values.push(current.trim());
    return values;
  });
  return {
    headers,
    rows
  };
}
function mapHeaderToKey(header: string): string | null {
  const normalized = header.toLowerCase().replace(/[^a-z0-9]/g, '');
  for (const col of TEMPLATE_COLUMNS) {
    const colNorm = col.label.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (normalized === colNorm) return col.key;
    const keyNorm = col.key.toLowerCase();
    if (normalized === keyNorm) return col.key;
  }
  return null;
}
function validateRow(data: Record<string, string>): string[] {
  const errors: string[] = [];
  if (!data.supplierName) errors.push('Supplier Name is required');
  if (!data.supplierEmail) errors.push('Supplier Email is required');
  if (data.supplierEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.supplierEmail)) {
    errors.push('Invalid email format');
  }
  if (!data.billNumber) errors.push('Bill Number is required');
  if (!data.amount) {
    errors.push('Amount is required');
  } else if (isNaN(Number(data.amount)) || Number(data.amount) <= 0) {
    errors.push('Amount must be a positive number');
  }
  if (!data.dueDate) {
    errors.push('Due Date is required');
  } else if (!/^\d{4}-\d{2}-\d{2}$/.test(data.dueDate)) {
    errors.push('Due Date must be in YYYY-MM-DD format');
  }
  if (data.issueDate && !/^\d{4}-\d{2}-\d{2}$/.test(data.issueDate)) {
    errors.push('Issue Date must be in YYYY-MM-DD format');
  }
  return errors;
}
export function BulkBillUpload() {
  const {
    t
  } = useTranslation('bills');
  const navigate = useNavigate();
  const [step, setStep] = useState<'upload' | 'preview' | 'success'>('upload');
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [fileName, setFileName] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [parseError, setParseError] = useState('');
  const validRows = useMemo(() => parsedRows.filter(r => r.errors.length === 0), [parsedRows]);
  const errorRows = useMemo(() => parsedRows.filter(r => r.errors.length > 0), [parsedRows]);
  const totalAmount = useMemo(() => validRows.reduce((sum, r) => sum + (Number(r.data.amount) || 0), 0), [validRows]);
  const handleDownloadTemplate = () => {
    const csv = generateCSVTemplate();
    const blob = new Blob([csv], {
      type: 'text/csv'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bill_upload_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };
  const processFile = useCallback((file: File) => {
    setParseError('');
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = e => {
      const text = e.target?.result as string;
      const {
        headers,
        rows
      } = parseCSV(text);
      if (headers.length === 0 || rows.length === 0) {
        setParseError('The file appears to be empty or has no data rows.');
        return;
      }
      const keyMap: (string | null)[] = headers.map(mapHeaderToKey);
      const unmapped = headers.filter((_, i) => keyMap[i] === null);
      if (unmapped.length > 0 && keyMap.filter(Boolean).length === 0) {
        setParseError(`Could not match any columns. Please use the template format. Unrecognized headers: ${unmapped.join(', ')}`);
        return;
      }
      const parsed: ParsedRow[] = rows.map((values, idx) => {
        const data: Record<string, string> = {};
        keyMap.forEach((key, i) => {
          if (key && values[i] !== undefined) {
            data[key] = values[i];
          }
        });
        return {
          id: `row-${idx}`,
          data,
          errors: validateRow(data)
        };
      });
      setParsedRows(parsed);
      setStep('preview');
    };
    reader.readAsText(file);
  }, []);
  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  }, [processFile]);
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = () => setIsDragging(false);
  const removeRow = (id: string) => {
    setParsedRows(prev => prev.filter(r => r.id !== id));
  };
  const handleImport = () => {
    // In a real app, this would POST to an API and create bills
    setStep('success');
  };
  const handleReset = () => {
    setStep('upload');
    setParsedRows([]);
    setFileName('');
    setParseError('');
  };
  const formatCurrency = (amount: number) => {
    return formatCurrencyAmount(amount, {
      fractionDigits: 2
    });
  };
  return <div className="max-w-6xl mx-auto space-y-6 pb-12" data-id="element-4031">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-4032">
        <button onClick={() => navigate('/bills')} className="flex items-center text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-4033">
          <ArrowLeftIcon size={16} className="mr-2" data-id="element-4034" />
          {t('pages.BillApprovalWizard.back_to_bills')}
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-4035">
        <div className="px-8 py-6 border-b border-gray-100" data-id="element-4036">
          <h1 className="text-xl font-semibold text-gray-900" data-id="element-4037">
            {t('pages.BulkBillUpload.bulk_upload_bills')}
          </h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-4038">
            {t('pages.BulkBillUpload.upload_a_csv_file_to_add')}
          </p>
        </div>

        <div className="p-8" data-id="element-4039">
          {/* ========== UPLOAD STEP ========== */}
          {step === 'upload' && <div className="space-y-8" data-id="element-4040">
              {/* Template download */}
              <div className="bg-gray-50 rounded-lg border border-gray-200 p-5 flex items-center justify-between" data-id="element-4041">
                <div className="flex items-center" data-id="element-4042">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center mr-4" data-id="element-4043">
                    <FileSpreadsheetIcon size={20} className="text-green-700" data-id="element-4044" />
                  </div>
                  <div data-id="element-4045">
                    <p className="text-sm font-medium text-gray-900" data-id="element-4046">
                      {t('pages.BulkBillUpload.download_template')}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5" data-id="element-4047">
                      {t('pages.BulkBillUpload.use_this_csv_template_to_format')}
                    </p>
                  </div>
                </div>
                <button onClick={handleDownloadTemplate} className="flex items-center px-4 py-2 text-sm font-medium text-[#B38A00] bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors shadow-sm" data-id="element-4048">
                  <DownloadIcon size={16} className="mr-2" data-id="element-4049" />
                  {t('pages.BulkBillUpload.download_csv')}
                </button>
              </div>

              {/* Dropzone */}
              <div onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave} className={`relative border-2 border-dashed rounded-xl p-12 text-center transition-all ${isDragging ? 'border-[#FFC70A] bg-[#FFC70A]/5' : 'border-gray-300 hover:border-gray-400 bg-gray-50/50'}`} data-id="element-4050">
                <input type="file" accept=".csv,.txt" onChange={handleFileInput} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" data-id="element-4051" />

                <div className="flex flex-col items-center" data-id="element-4052">
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-4 ${isDragging ? 'bg-[#FFC70A]/10' : 'bg-gray-100'}`} data-id="element-4053">
                    <UploadCloudIcon size={28} className={isDragging ? 'text-[#B38A00]' : 'text-gray-400'} data-id="element-4054" />
                  </div>
                  <p className="text-sm font-medium text-gray-700 mb-1" data-id="element-4055">
                    {isDragging ? 'Drop your file here' : 'Drag and drop your CSV file here'}
                  </p>
                  <p className="text-xs text-gray-500 mb-4" data-id="element-4056">
                    {t('pages.BulkBillUpload.or_click_to_browse')}
                  </p>
                  <span className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-md" data-id="element-4057">
                    {t('pages.BulkBillUpload.supported_csv')}
                  </span>
                </div>
              </div>

              {parseError && <div className="flex items-start p-4 bg-red-50 border border-red-200 rounded-lg" data-id="element-4058">
                  <AlertCircleIcon size={18} className="text-red-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-4059" />

                  <div data-id="element-4060">
                    <p className="text-sm font-medium text-red-800" data-id="element-4061">
                      {t('pages.BulkBillUpload.upload_error')}
                    </p>
                    <p className="text-xs text-red-600 mt-0.5" data-id="element-4062">{parseError}</p>
                  </div>
                </div>}

              {/* Field reference */}
              <div data-id="element-4063">
                <h3 className="text-sm font-medium text-gray-900 mb-3" data-id="element-4064">
                  {t('pages.BulkBillUpload.expected_fields')}
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-id="element-4065">
                  {TEMPLATE_COLUMNS.map(col => <div key={col.key} className="flex items-center text-xs text-gray-600 py-1.5 px-2.5 bg-gray-50 rounded-md border border-gray-100" data-id="element-4066">
                      <span className="font-medium text-gray-800 mr-1" data-id="element-4067">
                        {col.label}
                      </span>
                      {col.required && <span className="text-red-400 text-[10px]" data-id="element-4068">*</span>}
                    </div>)}
                </div>
              </div>
            </div>}

          {/* ========== PREVIEW STEP ========== */}
          {step === 'preview' && <div className="space-y-6" data-id="element-4069">
              {/* Summary bar */}
              <div className="flex items-center justify-between bg-gray-50 rounded-lg border border-gray-200 px-5 py-4" data-id="element-4070">
                <div className="flex items-center space-x-6" data-id="element-4071">
                  <div className="flex items-center text-sm" data-id="element-4072">
                    <FileSpreadsheetIcon size={16} className="text-gray-400 mr-2" data-id="element-4073" />

                    <span className="font-medium text-gray-900" data-id="element-4074">
                      {fileName}
                    </span>
                  </div>
                  <div className="flex items-center space-x-4 text-sm" data-id="element-4075">
                    <span className="text-gray-600" data-id="element-4076">
                      <span className="font-semibold text-gray-900" data-id="element-4077">
                        {parsedRows.length}
                      </span>{' '}
                      {t('pages.BulkBillUpload.rows_total')}
                    </span>
                    <span className="text-green-600" data-id="element-4078">
                      <span className="font-semibold" data-id="element-4079">{validRows.length}</span>{' '}
                      {t('pages.BulkBillUpload.valid')}
                    </span>
                    {errorRows.length > 0 && <span className="text-red-600" data-id="element-4080">
                        <span className="font-semibold" data-id="element-4081">
                          {errorRows.length}
                        </span>{' '}
                        {t('pages.BulkBillUpload.with_errors')}
                      </span>}
                  </div>
                </div>
                <button onClick={handleReset} className="text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors" data-id="element-4082">
                  {t('pages.BulkBillUpload.upload_different_file')}
                </button>
              </div>

              {/* Total amount banner */}
              {validRows.length > 0 && <div className="bg-[#FFC70A]/5 border border-[#FFC70A]/20 rounded-lg px-5 py-4 flex items-center justify-between" data-id="element-4083">
                  <span className="text-sm text-gray-700" data-id="element-4084">
                    {t('pages.BulkBillUpload.total_amount_across')}{' '}
                    <span className="font-semibold" data-id="element-4085">{validRows.length}</span>{' '}
                    {t('pages.BulkBillUpload.valid_bills')}
                  </span>
                  <span className="text-lg font-bold text-gray-900" data-id="element-4086">
                    {t('pages.BillApprovalWizard.usd')}{' '}
                    {formatCurrency(totalAmount)}
                  </span>
                </div>}

              {/* Error rows */}
              {errorRows.length > 0 && <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-id="element-4087">
                  <div className="flex items-center mb-3" data-id="element-4088">
                    <AlertCircleIcon size={16} className="text-red-500 mr-2" data-id="element-4089" />
                    <span className="text-sm font-medium text-red-800" data-id="element-4090">
                      {errorRows.length} {t('pages.BulkBillUpload.row')}
                      {errorRows.length > 1 ? 's' : ''}{' '}
                      {t('pages.BulkBillUpload.with_validation_errors')}
                    </span>
                  </div>
                  <p className="text-xs text-red-600 mb-3" data-id="element-4091">
                    {t('pages.BulkBillUpload.these_rows_have_issues_and_will')}
                  </p>
                  <div className="space-y-2" data-id="element-4092">
                    {errorRows.map(row => <div key={row.id} className="flex items-center justify-between bg-white rounded-md border border-red-200 px-4 py-2.5" data-id="element-4093">
                        <div className="flex-1 min-w-0" data-id="element-4094">
                          <span className="text-sm font-medium text-gray-900 mr-3" data-id="element-4095">
                            {row.data.billNumber || '(no bill number)'}
                          </span>
                          <span className="text-xs text-gray-500 mr-3" data-id="element-4096">
                            {row.data.supplierName || '(no supplier)'}
                          </span>
                          <span className="text-xs text-red-600" data-id="element-4097">
                            {row.errors.join('; ')}
                          </span>
                        </div>
                        <button onClick={() => removeRow(row.id)} className="p-1.5 text-gray-400 hover:text-red-500 rounded-md hover:bg-red-50 transition-colors ml-3" data-id="element-4098">
                          <TrashIcon size={14} data-id="element-4099" />
                        </button>
                      </div>)}
                  </div>
                </div>}

              {/* Preview table */}
              {validRows.length > 0 && <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-4100">
                  <div className="overflow-x-auto" data-id="element-4101">
                    <table className="w-full text-left" data-id="element-4102">
                      <thead data-id="element-4103">
                        <tr className="bg-gray-50 border-b border-gray-200" data-id="element-4104">
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-10" data-id="element-4105">
                            #
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-4106">
                            {t('pages.BillFinancingWizard.bill_number')}
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-4107">
                            {t('pages.BillFinancingWizard.supplier')}
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-4108">
                            {t('pages.BulkBillUpload.email')}
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right" data-id="element-4109">
                            {t('pages.BillApprovalWizard.amount')}
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-4110">
                            {t('pages.BillForm.currency')}
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-4111">
                            {t('pages.BillDetail.due_date')}
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-4112">
                            {t('pages.BulkBillUpload.description')}
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-12" data-id="element-4113"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100" data-id="element-4114">
                        {validRows.map((row, idx) => <tr key={row.id} className="hover:bg-gray-50 transition-colors" data-id="element-4115">
                            <td className="px-4 py-3 text-xs text-gray-400" data-id="element-4116">
                              {idx + 1}
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-4117">
                              {row.data.billNumber || '-'}
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-4118">
                              {row.data.supplierName || '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600" data-id="element-4119">
                              {row.data.supplierEmail || '-'}
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right font-mono" data-id="element-4120">
                              {row.data.amount ? formatCurrency(Number(row.data.amount)) : '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600" data-id="element-4121">
                              {row.data.currency || getTenant().currency.code}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600" data-id="element-4122">
                              {row.data.dueDate || '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500 max-w-[200px] truncate" data-id="element-4123">
                              {row.data.description || '-'}
                            </td>
                            <td className="px-4 py-3" data-id="element-4124">
                              <button onClick={() => removeRow(row.id)} className="p-1.5 text-gray-400 hover:text-red-500 rounded-md hover:bg-gray-100 transition-colors" data-id="element-4125">
                                <TrashIcon size={14} data-id="element-4126" />
                              </button>
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>}

              {parsedRows.length === 0 && <div className="text-center py-12 text-gray-500 text-sm" data-id="element-4127">
                  {t('pages.BulkBillUpload.all_rows_have_been_removed_upload')}
                </div>}

              {/* Actions */}
              <div className="flex justify-between items-center pt-4 border-t border-gray-100" data-id="element-4128">
                <button onClick={handleReset} className="px-5 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-4129">
                  {t('pages.BillApprovalWizard.cancel')}
                </button>
                <button onClick={handleImport} disabled={validRows.length === 0} className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-colors shadow-sm ${validRows.length > 0 ? 'bg-[#FFC70A] hover:bg-[#D9A800] text-gray-900' : 'bg-gray-300 text-white cursor-not-allowed'}`} data-id="element-4130">
                  {t('pages.BulkBillUpload.import')} {validRows.length}{' '}
                  {t('pages.BillApprovalWizard.bill')}
                  {validRows.length !== 1 ? 's' : ''}
                </button>
              </div>
            </div>}

          {/* ========== SUCCESS STEP ========== */}
          {step === 'success' && <div className="text-center py-16" data-id="element-4131">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6" data-id="element-4132">
                <CheckCircleIcon size={32} className="text-green-600" data-id="element-4133" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-4134">
                {t('pages.BulkBillUpload.bills_imported_successfully')}
              </h2>
              <p className="text-sm text-gray-500 mb-2" data-id="element-4135">
                {validRows.length} {t('pages.BillApprovalWizard.bill')}
                {validRows.length !== 1 ? 's have' : ' has'}{' '}
                {t('pages.BulkBillUpload.been_added_to_your_account')}
              </p>
              <p className="text-sm text-gray-700 font-medium mb-8" data-id="element-4136">
                {t('pages.BulkBillUpload.total_usd')}{' '}
                {formatCurrency(totalAmount)}
              </p>
              <div className="flex justify-center gap-3" data-id="element-4137">
                <button onClick={handleReset} className="px-5 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-4138">
                  {t('pages.BulkBillUpload.upload_more')}
                </button>
                <button onClick={() => navigate('/bills')} className="px-5 py-2.5 bg-[#FFC70A] hover:bg-[#D9A800] text-gray-900 text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-4139">
                  {t('pages.BulkBillUpload.go_to_bills')}
                </button>
              </div>
            </div>}
        </div>
      </div>
    </div>;
}